import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

import Home from "@/pages/Home";
import Search from "@/pages/Search";
import CourseDetail from "@/pages/CourseDetail";
import Universities from "@/pages/Universities";
import UniversityDetail from "@/pages/UniversityDetail";
import Countries from "@/pages/Countries";
import CountryDetail from "@/pages/CountryDetail";
import Categories from "@/pages/Categories";
import Pricing from "@/pages/Pricing";
import StaticPage from "@/pages/StaticPage";
import ForUniversities from "@/pages/ForUniversities";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import MyInstitution from "@/pages/dashboard/MyInstitution";
import MyCourses from "@/pages/dashboard/MyCourses";
import MyListing from "@/pages/dashboard/MyListing";
import DashPayments from "@/pages/dashboard/DashPayments";
import PaymentSuccess from "@/pages/dashboard/PaymentSuccess";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminInstitutions from "@/pages/admin/AdminInstitutions";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminCountries from "@/pages/admin/AdminCountries";
import AdminCities from "@/pages/admin/AdminCities";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSpecialisations from "@/pages/admin/AdminSpecialisations";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPages from "@/pages/admin/AdminPages";
import AdminReports from "@/pages/admin/AdminReports";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <div className="flex-1"><Outlet /></div>
      <Footer />
    </div>
  );
}

function Protected({ role }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return <Outlet />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Search />} />
            <Route path="/courses/:courseSlug/:instSlug" element={<CourseDetail />} />
            <Route path="/courses/:categorySlug" element={<Search />} />
            <Route path="/universities" element={<Universities />} />
            <Route path="/universities/:slug" element={<UniversityDetail />} />
            <Route path="/countries" element={<Countries />} />
            <Route path="/countries/:slug" element={<CountryDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<StaticPage slug="about" />} />
            <Route path="/for-universities" element={<ForUniversities />} />
            <Route path="/page/:slug" element={<StaticPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          <Route element={<Protected role="institution" />}>
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardHome />} />
              <Route path="institution" element={<MyInstitution />} />
              <Route path="courses" element={<MyCourses />} />
              <Route path="listing" element={<MyListing />} />
              <Route path="payments" element={<DashPayments />} />
            </Route>
          </Route>

          <Route element={<Protected role="admin" />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="institutions" element={<AdminInstitutions />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="countries" element={<AdminCountries />} />
              <Route path="cities" element={<AdminCities />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="specialisations" element={<AdminSpecialisations />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="reports" element={<AdminReports />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
